/**
 * What the table thought of a recipe, shown where the cook can see it.
 *
 * One pill per person: a small initial says WHO, a glass disc says WHAT they
 * thought. Side by side, never stacked — the disc is 25px, larger than the
 * initial it would otherwise hang off, and a badge that covers the letter
 * defeats the point of naming who liked it.
 *
 * This replaced a five-star row. Five stars over three possible answers was a
 * display promising precision the input never had (solo, only 5/3/1 were
 * reachable), it looked like a tappable input but only ever opened the sheet,
 * and averaging four opinions into one number threw away the interesting part —
 * "3 stars" could be four shrugs or two loves and two refusals.
 *
 * Colour discipline (DESIGN.md): every fill here is a tonal shift, white-alpha
 * on a photo scrim and bgSunk on paper. The only colour is the glyph inside the
 * disc, which is the semantic use the design system already sanctions. An
 * earlier pass filled each avatar with approvalYes/approvalNo and put four
 * saturated circles on a food photo; terracotta has to stay the only strong
 * accent.
 */
import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaTypography } from '../../styles/theme';
import { selectRecipeVerdicts, type VerdictValue } from '../../redux/recipeMetadataSlice';
import type { RootState } from '../../redux/store';
import { useHouseholdEaters, type TableMember } from '../../hooks/useHouseholdEaters';

/** One glyph per verdict, defined once so the sheet and the cards agree. */
export const VERDICT_ICON: Record<VerdictValue, keyof typeof Feather.glyphMap> = {
  love: 'heart',
  ok: 'minus',
  no: 'thumbs-down',
};

/** Ink inside the disc. Never a fill — see the colour note above. */
const VERDICT_INK: Record<VerdictValue, string> = {
  love: Paprika.approvalYes,
  ok: Paprika.textMuted,
  no: Paprika.approvalNo,
};

/**
 * Opaque enough to read a 14px glyph against a photo. Paprika.glassWhite (0.72)
 * is tuned for chips over blurred backgrounds and loses the thin strokes here.
 */
const DISC_ON_SCRIM = 'rgba(255, 255, 255, 0.94)';

export type VerdictAvatarsVariant =
  | 'card'
  | 'mobile'
  | 'dense'
  | 'paper'
  | 'paperDense';

interface VariantSpec {
  initial: number;
  disc: number;
  /** Pills past this are collapsed into a "+N" chip. */
  max: number;
  onPaper: boolean;
  /** Dense cards drop the pill background — there is no room for it. */
  bare: boolean;
}

/**
 * Sizes are per surface, not a single scale, because the containers differ by
 * an order of magnitude. Three 22+25 pills are ~179px and overflow the 206px
 * desktop card once the stamp's -6deg tilt is applied, which is why `max` is 2
 * there and 3 on the 300px mobile row.
 */
const VARIANTS: Record<VerdictAvatarsVariant, VariantSpec> = {
  card: { initial: 22, disc: 25, max: 2, onPaper: false, bare: false },
  mobile: { initial: 26, disc: 25, max: 3, onPaper: false, bare: false },
  dense: { initial: 20, disc: 16, max: 2, onPaper: false, bare: true },
  paper: { initial: 22, disc: 25, max: 3, onPaper: true, bare: false },
  // Compact meta rows (SecondaryMealRow, SlotExtraCard) sit on paper, not on a
  // photo, and share their line with a time chip and a servings count.
  paperDense: { initial: 18, disc: 16, max: 2, onPaper: true, bare: true },
};

interface Props {
  recipeId: string;
  variant?: VerdictAvatarsVariant;
  /**
   * Show "Qui a aimé ?" when nobody has answered. The cooked stamp wants it —
   * an unanswered card should invite rather than look broken — the recipe
   * detail line does not.
   */
  showCaption?: boolean;
}

export function VerdictAvatars({ recipeId, variant = 'card', showCaption = false }: Props) {
  const { t } = useTranslation();
  const spec = VARIANTS[variant];
  const { table } = useHouseholdEaters(t('household.eaters.selfLabel'));
  const verdicts = useSelector((s: RootState) => selectRecipeVerdicts(s, recipeId));

  const { shown, overflow, anyAnswered } = useMemo(() => {
    // Answered first: with only two pills of room, whoever spoke is the news.
    const ordered = [...table].sort((a, b) => {
      const av = verdicts[a.id] ? 0 : 1;
      const bv = verdicts[b.id] ? 0 : 1;
      return av - bv;
    });
    return {
      shown: ordered.slice(0, spec.max),
      overflow: Math.max(0, ordered.length - spec.max),
      anyAnswered: ordered.some((m) => !!verdicts[m.id]),
    };
  }, [table, verdicts, spec.max]);

  if (table.length === 0) return null;

  return (
    <View style={styles.wrap}>
      <View style={styles.row}>
        {shown.map((member) => (
          <VerdictPill
            key={member.id}
            member={member}
            value={verdicts[member.id]?.value}
            spec={spec}
            label={
              verdicts[member.id]
                ? `${member.name} — ${t(`household.verdict.${verdicts[member.id].value}`)}`
                : `${member.name} — ${t('household.verdict.unanswered')}`
            }
          />
        ))}
        {overflow > 0 && (
          <View style={[styles.more, spec.onPaper ? styles.morePaper : styles.moreScrim]}>
            <Text
              style={[
                styles.moreText,
                { color: spec.onPaper ? Paprika.textSecondary : '#ffffff' },
              ]}
            >
              {t('household.verdict.overflow', { count: overflow })}
            </Text>
          </View>
        )}
      </View>
      {showCaption && !anyAnswered && (
        <Text style={styles.caption}>{t('household.verdict.whoLiked')}</Text>
      )}
    </View>
  );
}

function VerdictPill({
  member,
  value,
  spec,
  label,
}: {
  member: TableMember;
  value?: VerdictValue;
  spec: VariantSpec;
  label: string;
}) {
  const answered = !!value;
  const initialSize = {
    width: spec.initial,
    height: spec.initial,
    borderRadius: spec.initial / 2,
    fontSize: Math.round(spec.initial * 0.42),
  };

  // Unanswered carries no pill and no disc: a dashed outline reads as "waiting"
  // where a filled circle would read as an opinion nobody actually gave. On
  // paper this matters twice over — an earlier pass gave "Bof" and "no answer"
  // the same bgSunk fill and they were indistinguishable.
  if (!answered) {
    return (
      <View
        style={[
          styles.initial,
          initialSize,
          styles.initialEmpty,
          spec.onPaper ? styles.initialEmptyPaper : styles.initialEmptyScrim,
        ]}
        accessible
        accessibilityLabel={label}
      >
        <Text
          style={[
            styles.initialText,
            { fontSize: initialSize.fontSize },
            { color: spec.onPaper ? Paprika.textMuted : 'rgba(255,255,255,0.45)' },
          ]}
        >
          {member.initial}
        </Text>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.pill,
        spec.bare && styles.pillBare,
        !spec.bare && (spec.onPaper ? styles.pillPaper : styles.pillScrim),
      ]}
      accessible
      accessibilityLabel={label}
    >
      <View
        style={[
          styles.initial,
          initialSize,
          spec.onPaper ? styles.initialPaper : styles.initialScrim,
        ]}
      >
        <Text
          style={[
            styles.initialText,
            { fontSize: initialSize.fontSize },
            { color: spec.onPaper ? Paprika.textSecondary : '#ffffff' },
          ]}
        >
          {member.initial}
        </Text>
      </View>
      <View
        style={[
          styles.disc,
          {
            width: spec.disc,
            height: spec.disc,
            borderRadius: spec.disc / 2,
            backgroundColor: spec.onPaper ? Paprika.surfaceLowest : DISC_ON_SCRIM,
          },
        ]}
      >
        <Feather
          name={VERDICT_ICON[value]}
          size={Math.round(spec.disc * 0.56)}
          color={VERDICT_INK[value]}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    alignItems: 'center',
    gap: 2,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    borderRadius: 999,
    paddingVertical: 2,
    paddingLeft: 2,
    paddingRight: 3,
  },
  pillScrim: {
    backgroundColor: 'rgba(255, 255, 255, 0.14)',
  },
  pillPaper: {
    backgroundColor: Paprika.bgSunk,
  },
  pillBare: {
    backgroundColor: 'transparent',
    paddingVertical: 0,
    paddingLeft: 0,
    paddingRight: 0,
    gap: 2,
  },
  initial: {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  initialScrim: {
    backgroundColor: 'rgba(255, 255, 255, 0.22)',
  },
  initialPaper: {
    backgroundColor: '#e4dcd0',
  },
  initialEmpty: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'dashed',
  },
  initialEmptyScrim: {
    borderColor: 'rgba(255, 255, 255, 0.35)',
  },
  initialEmptyPaper: {
    borderColor: Paprika.outlineVariant,
  },
  initialText: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
  },
  disc: {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    // Warm shadow only — never rgba(0,0,0,x).
    shadowColor: 'rgba(82, 68, 60, 0.28)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 3,
    elevation: 2,
  },
  more: {
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  moreScrim: {
    backgroundColor: 'rgba(255, 255, 255, 0.22)',
  },
  morePaper: {
    backgroundColor: Paprika.bgSunk,
  },
  moreText: {
    ...PaprikaTypography.labelSmall,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
  },
  caption: {
    ...PaprikaTypography.labelSmall,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.78)',
  },
});

export default VerdictAvatars;
